import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { AxiosError, AxiosResponse } from "axios";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { addSeletedAndDetectionType } from "./addSeletedAndDetectionType";
import { getAlertMP4 } from "./getAlertMP4";
import {
  AlertData,
  MutedAlertSites,
  MutedAlertCameras,
} from "./alertsInterfaces";
import { yyyymmddGenerator } from "../../Analytics/AnalyticsUtils/yyyymmddGenerator";
import { getActiveIgnoreZones } from "./getActiveIgnoreZones";
import { getAlertsRefresh } from "./getAlertsRefresh";
import {
  APIErrorContext,
  universalAPIErrorHandler,
} from "../../../utils/universalAPIErrorHandler";
import { getParentAccounts } from "../../../utils/getParentAccounts";
import {
  clearAlertsFetchError,
  recordAlertsFetchError,
} from "../AlertsStore/alertsFetchErrorStore";
import { recordGroupAlertSnapshot } from "../AlertsStore/alertsScopeStore";
import { timeSlicer } from "../timeSlicer";
import { createLabels } from "./createLabels";
import { checkForRedirectAndFetchMP4 } from "./checkForRedirectAndFetchMP4";
import { isCurrentDate } from "../EndDateDropDownAlerts";
import { alertService } from "../../../../services/api/alertService";
import { useAppStore } from "../../../../appStore";
import { logger } from "../../../../utils/logger";
import { handleAlertButtonsFill } from "./handleAlertButtonsFill";
import { isAlertMuted, hideMutedFromView } from "./isAlertMuted";

export const getAlerts = (
  setAlertData: Dispatcher<AlertData[]>,
  navigate: NavigateFunction,
  setSelectedRow: Dispatcher<any>,
  setPageLoading: Dispatcher<boolean>,
  setSeeMoreURL: Dispatcher<string>,
  setMP4URL: Dispatcher<string>,
  userFilters: any,
  setThumbnailVideos: any,
  setDateOutsideRange: Dispatcher<boolean>,
  setVideoClipLoading: Dispatcher<boolean>,
  setAlertsToDisplay: Dispatcher<boolean>,
  setSelectedAlertCameraInfo: any,
  setIgnoreZonesSelectedAlert: any,
  setAlertsIndexOrder: any,
  setRefreshInterval: any,
  setFetchingFilteredData: any,
  setCurrentName: any,
  setPage: any,
  setSelectedButtonsFilled: any,
  setUserFilters: any,
  cameraOptions: any,
  siteOptions: any,
  setRefreshingData: any,
  pageLoadedNumber: any,
  refreshInterval: number,
  setParentAccountsLoading: any,
  setParentAccounts: any,
  setParentIds: any,
  setParentNames: any,
  setRefreshingToken: Dispatcher<boolean>,
  refreshingToken: boolean,
  setAlertLabels: Dispatcher<string[]>,
  setPeriodHours: Dispatcher<any>,
  setFiltersApplied: Dispatcher<boolean>,
  setForwardArrowDisabled: Dispatcher<boolean>,
  setBackArrowDisabled: Dispatcher<boolean>,
  setShowRefreshButton: Dispatcher<boolean>,
  setRowsSortable: Dispatcher<boolean>,
  setAlertsRefreshing: Dispatcher<boolean>,
  getCurrentSelectedRowKey: () => string | null,
  mutedAlertSites: MutedAlertSites,
  mutedAlertCameras: MutedAlertCameras,
  setVideoProcessing?: Dispatcher<boolean>
) => {
  const useAsyncGeneration = !userFilters?.has_video;
  const cameraIds = new Map();
  cameraOptions.forEach((object: any) => {
    cameraIds.set(object.camera_name, object.id);
  });

  const siteIds = new Map();
  siteOptions.forEach((object: any) => {
    siteIds.set(object.name, object.id);
  });

  const parentAccounts = JSON.parse(localStorage.parentAccounts);

  const parentIds = new Map();
  parentAccounts.forEach((object: any) => {
    parentIds.set(object.name, object.id);
  });

  const { setNavigatedToAlertsFromAnalytics, navigatedToAlertsFromAnalytics } =
    useAppStore.getState();

  const AxiosUI = createMonitoringAPICall(``);
  const retryFunction = () => {
    getAlerts(
      setAlertData,
      navigate,
      setSelectedRow,
      setPageLoading,
      setSeeMoreURL,
      setMP4URL,
      userFilters,
      setThumbnailVideos,
      setDateOutsideRange,
      setVideoClipLoading,
      setAlertsToDisplay,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      setAlertsIndexOrder,
      setRefreshInterval,
      setFetchingFilteredData,
      setCurrentName,
      setPage,
      setSelectedButtonsFilled,
      setUserFilters,
      cameraOptions,
      siteOptions,
      setRefreshingData,
      pageLoadedNumber,
      refreshInterval,
      setParentAccountsLoading,
      setParentAccounts,
      setParentIds,
      setParentNames,
      setRefreshingToken,
      refreshingToken,
      setAlertLabels,
      setPeriodHours,
      setFiltersApplied,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      setRowsSortable,
      setAlertsRefreshing,
      getCurrentSelectedRowKey,
      mutedAlertSites,
      mutedAlertCameras,
      setVideoProcessing
    );
  };

  let searchParamsString = ``;

  const startDate = yyyymmddGenerator(userFilters.start_date);
  const endDate = yyyymmddGenerator(userFilters.end_date);

  if (startDate) {
    searchParamsString += `&start=${startDate}`;
  }

  if (endDate) {
    searchParamsString += `&end=${endDate}`;
  }

  if (userFilters.parent_group) {
    if (userFilters.parent_group !== "undefined") {
      searchParamsString += `&group_id=${userFilters.parent_group}`;
    }
  }

  if (userFilters.site) {
    if (userFilters.site !== "undefined") {
      searchParamsString += `&customer_id=${userFilters.site}`;
    }
  }

  if (userFilters.camera) {
    if (userFilters.camera !== "undefined") {
      searchParamsString += `&camera_id=${userFilters.camera}`;
    }
  }

  if (startDate && endDate) {
    if (
      startDate.toString() === endDate.toString() &&
      userFilters.start_time !== null
    ) {
      searchParamsString += `&start_time=${timeSlicer(userFilters.start_time)}`;
    }

    if (
      startDate.toString() === endDate.toString() &&
      userFilters.end_time !== null
    ) {
      searchParamsString += `&end_time=${timeSlicer(userFilters.end_time)}`;
    }
  }

  if (userFilters.has_video) {
    searchParamsString += `&has_video=true`;
  }

  // let noAlertsOutsideRange = true;
  const getAlertsFunction = async (refreshInt: number) => {
    const filterType = userFilters.filter_type;

    clearAlertsFetchError();
    try {
      const responseData = await alertService.getAlerts({
        filterType,
        searchParamsString,
      });

      // Hide muted alerts from the live (Unresolved) queue only — the
      // Resolved queue retains them, including for the muting user.
      const filteredRecords = hideMutedFromView(filterType)
        ? responseData.records.filter((row: any) => {
            return !isAlertMuted(row, mutedAlertSites, mutedAlertCameras);
          })
        : responseData.records;

      // Update responseData.records with filtered data
      // responseData.records = filteredRecords;

      recordGroupAlertSnapshot(
        responseData,
        userFilters.parent_group,
        filterType,
        searchParamsString
      );

      getParentAccounts(
        setParentAccountsLoading,
        setParentAccounts,
        setParentIds,
        setParentNames,
        navigate
      );
      if (responseData.parameters) {
        if (responseData.parameters.period_hours) {
          setPeriodHours(responseData.parameters.period_hours);
        }
      }
      if (navigatedToAlertsFromAnalytics) {
        setAlertsRefreshing(false);
        setNavigatedToAlertsFromAnalytics(false);
      }
      setRowsSortable(true);
      if (filteredRecords[0]) {
        setSelectedRow(filteredRecords[0]);
        handleAlertButtonsFill(filteredRecords[0], setSelectedButtonsFilled);
        localStorage.setItem(
          "selectedRowAlerts",
          JSON.stringify(filteredRecords[0])
        );
        if (filteredRecords[0].camera_pk) {
          getActiveIgnoreZones(
            filteredRecords[0].camera_pk,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            navigate
          );
        } else {
          setSelectedAlertCameraInfo({});
          setIgnoreZonesSelectedAlert([]);
        }
      }
      setAlertData(
        addSeletedAndDetectionType(
          responseData.records,
          setAlertsIndexOrder,
          hideMutedFromView(filterType) ? mutedAlertSites : [],
          hideMutedFromView(filterType) ? mutedAlertCameras : []
        ) as AlertData[]
      );
      if (filteredRecords[0]) {
        checkForRedirectAndFetchMP4(
          filteredRecords[0],
          navigate,
          setMP4URL,
          setVideoClipLoading,
          setShowRefreshButton,
          false,
          getCurrentSelectedRowKey,
          setVideoProcessing,
          useAsyncGeneration
        );
        setFetchingFilteredData(false);
        setPageLoading(false);
        setFiltersApplied(true);
        setAlertsToDisplay(true);
        setSeeMoreURL(filteredRecords[0].alert_url);
        if (responseData.records.length === 1) {
          setForwardArrowDisabled(true);
        }
        createLabels(responseData.records, setAlertLabels);
      } else {
        setPageLoading(false);
        setFiltersApplied(true);
      }
      if (
        localStorage.tokenRefreshing === "false" ||
        localStorage.tokenRefreshing === undefined
      ) {
        let dataRecordsTimeStamp = "";
        if (filteredRecords[0]) {
          dataRecordsTimeStamp = filteredRecords[0].approx_capture_timestamp;
        }
        if (userFilters.end_date && !isCurrentDate(userFilters.end_date)) {
          setRowsSortable(true);
        } else {
          setRowsSortable(false);
        }
        if (
          ((!startDate || startDate === "") && (!endDate || endDate === "")) ||
          (startDate &&
            startDate !== "" &&
            !endDate &&
            isCurrentDate(startDate)) ||
          (endDate && endDate !== "" && !startDate && isCurrentDate(endDate)) ||
          (startDate &&
            startDate !== "" &&
            endDate &&
            endDate !== "" &&
            isCurrentDate(startDate) &&
            isCurrentDate(endDate))
        ) {
          if (!userFilters.end_time) {
            getAlertsRefresh(
              setAlertData,
              navigate,
              userFilters.parent_group,
              userFilters.site,
              userFilters.camera,
              yyyymmddGenerator(userFilters.start_date),
              yyyymmddGenerator(userFilters.end_date),
              userFilters.start_time,
              userFilters.end_time,
              userFilters.filter_type,
              setSelectedRow,
              setRefreshingData,
              setSelectedAlertCameraInfo,
              setIgnoreZonesSelectedAlert,
              setAlertsIndexOrder,
              refreshInt,
              setRefreshInterval,
              pageLoadedNumber,
              setSeeMoreURL,
              setMP4URL,
              setVideoClipLoading,
              setSelectedButtonsFilled,
              setAlertsToDisplay,
              setCurrentName,
              setPage,
              setDateOutsideRange,
              setFetchingFilteredData,
              setPageLoading,
              setUserFilters,
              userFilters,
              cameraOptions,
              siteOptions,
              dataRecordsTimeStamp,
              setRefreshingToken,
              refreshingToken,
              responseData.records.length,
              setBackArrowDisabled,
              setForwardArrowDisabled,
              setShowRefreshButton,
              setAlertsRefreshing,
              false,
              getCurrentSelectedRowKey,
              setVideoProcessing,
              useAsyncGeneration
            );
          } else {
            setAlertsRefreshing(false);
          }
        }
      }
    } catch (error) {
      universalAPIErrorHandler(
        error as AxiosError,
        navigate,
        retryFunction,
        (context?: APIErrorContext) => {
          // Surface the failure (SUP-445): without this a 400/5xx from the
          // backend rendered as "No alerts to display".
          recordAlertsFetchError(error, context);
          setPageLoading(false);
          setAlertsToDisplay(false);
        }
      );
    }
  };

  if (userFilters.parent_group) {
    AxiosUI.get(`group/${userFilters.parent_group}`).then(
      (response: AxiosResponse) => {
        if (response.data.refresh_interval_seconds) {
          getAlertsFunction(response.data.refresh_interval_seconds * 1000);
        } else {
          getAlertsFunction(refreshInterval);
        }
      },
      (reason: AxiosError) => {
        logger.log("error reason:", reason.message);
        getAlertsFunction(refreshInterval);
      }
    );
  } else {
    getAlertsFunction(refreshInterval);
  }
};
